import React, { useState, useEffect } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
/* eslint-disable import/named */
import { MultiValue } from 'react-select';

import { GetExamDto } from '../../../common/spdCore/autogenerated/spdApiClient';
import ValidationAlert from '../../../app/components/ValidationAlert';
import { ReactSelectOption } from '../Selects/SortableSelect';
import SupportingExaminationDictMultiSelector from '../SupportingExaminationDict/SupportingExaminationDictMultiSelector';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import numberFormatter from '../../../common/numberFormatter';

interface SynevoOrderModalProps {
  show: boolean;
  exam: GetExamDto;
  isLoading?: boolean;
  errors?: string[];
  onConfirm: (supportingExaminationDictIds: string[]) => void;
  onHide: () => void;
}

export const SynevoOrderModal: React.FC<SynevoOrderModalProps> = ({
  show,
  exam,
  isLoading = false,
  errors = [],
  onConfirm,
  onHide,
}) => {
  const spd = useSpdCore();
  const [selectedCost, setSelectedCost] = useState<{
    count: number;
    amount: string;
  } | null>(null);
  const [synevoIds, setSynevoIds] = useState<string[]>([]);
  const [selected, setSelected] = useState<ReactSelectOption[]>([]);

  useEffect(() => {
    if (!show) {
      return;
    }
    let mounted = true;
    spd.exams
      .getSupportingExaminationPreviewByExam(exam.id)
      .then(preview => {
        if (!mounted) {
          return;
        }
        setSynevoIds(preview.synevoIds || []);
      })
      .catch(() => {
        if (mounted) {
          setSynevoIds([]);
        }
      });
    return () => {
      mounted = false;
    };
  }, [show, exam.id, spd.exams]);

  useEffect(() => {
    if (show) {
      setSelected([]);
    }
  }, [show]);

  useEffect(() => {
    if (!show || selected.length === 0) {
      setSelectedCost(null);
      return;
    }
    let mounted = true;
    spd.synevo
      .getDictCost(
        selected.map(o => o.value),
        exam.id,
      )
      .then(cost => {
        if (mounted) {
          setSelectedCost(cost);
        }
      })
      .catch(() => {
        if (mounted) {
          setSelectedCost(null);
        }
      });
    return () => {
      mounted = false;
    };
  }, [selected, show, spd.synevo, exam.id]);

  const handleConfirm = () => {
    onConfirm(selected.map(o => o.value));
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>Utwórz zlecenie Synevo</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Wybierz badania pomocnicze, dla których zostanie utworzone zlecenie
          Synevo. Uwaga! nie można zlecić do Synevo dwa razy tego samego rodzaju
          badania
        </p>
        <SupportingExaminationDictMultiSelector
          multi={true}
          synevoOnly={true}
          filterIds={synevoIds.length ? synevoIds : undefined}
          selectedSupportingExamDict={selected}
          onChangeMulti={(options: MultiValue<ReactSelectOption>) =>
            setSelected((options || []) as ReactSelectOption[])
          }
        />
        {selectedCost && (
          <Alert variant="info" className="mt-3 mb-0">
            <div>
              Liczba wybranych badań Synevo:{' '}
              <strong>{selectedCost.count}</strong>
              {' na kwotę '}
              <strong>{numberFormatter(selectedCost.amount, true)}</strong>.
            </div>
          </Alert>
        )}
        <ValidationAlert
          show={errors.length > 0}
          errors={errors}
          className="mt-3 mb-0"
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Anuluj
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={isLoading || selected.length === 0}
        >
          {isLoading ? 'Tworzenie...' : 'Utwórz'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
